import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EyeIcon,
  ClockIcon,
  UserIcon,
  HomeIcon,
  CalendarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { anomalyService } from '../services/anomalyService';
import type { Anomaly, AnomalyStatistics } from '../types/anomaly';

export default function AnomalyViewPage() {
  const navigate = useNavigate();
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [statistics, setStatistics] = useState<AnomalyStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmResolveId, setConfirmResolveId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [anomaliesResponse, stats] = await Promise.allSettled([
        anomalyService.getUnresolvedAnomalies(),
        anomalyService.getStatistics(),
      ]);
      
      if (anomaliesResponse.status === 'fulfilled') {
        const data = anomaliesResponse.value;
        setAnomalies(data?.anomalies || (Array.isArray(data) ? data : []));
      } else {
        console.warn('Failed to load anomalies:', anomaliesResponse.reason);
        setAnomalies([]);
      }
      
      if (stats.status === 'fulfilled') {
        setStatistics(stats.value);
      } else {
        console.warn('Failed to load statistics:', stats.reason);
        setStatistics(null);
      }
    } catch (error) {
      console.error('Error loading anomalies:', error);
      setAnomalies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadData();
    } finally {
      setRefreshing(false);
    }
  };

  const handleResolve = async (anomalyId: string) => {
    setConfirmResolveId(anomalyId);
  };

  const confirmResolve = async () => {
    if (!confirmResolveId) return;

    try {
      setResolving(confirmResolveId);
      setResolveError(null);
      const currentUser = localStorage.getItem('username') || 'admin';

      await anomalyService.resolveAnomaly(confirmResolveId, {
        resolvedBy: currentUser,
      });

      setConfirmResolveId(null);
      await loadData();
    } catch (error) {
      console.error('Error resolving anomaly:', error);
      setResolveError('Erreur lors de la resolution de l\'anomalie. Veuillez reessayer.');
    } finally {
      setResolving(null);
    }
  };

  const handleViewDetails = (policeFileId: string) => {
    navigate(`/guests/${policeFileId}`);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSeverityColor = (daysSinceCheckIn: number): string => {
    if (daysSinceCheckIn >= 60) return 'text-red-600 bg-red-50 border-red-200';
    if (daysSinceCheckIn >= 45) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };

  const getSeverityBadge = (daysSinceCheckIn: number): string => {
    if (daysSinceCheckIn >= 60) return 'Critique';
    if (daysSinceCheckIn >= 45) return 'Élevé';
    return 'Moyen';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Anomalies Détectées</h1>
                <p className="text-gray-600 mt-1">
                  Fiches police nécessitant une attention particulière
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Non Résolues</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statistics.unresolvedCount || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Résolues</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statistics.resolvedCount || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Client Présent</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statistics.guestStillPresentCount || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Jours Moyens</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Math.round(statistics.avgDaysSinceCheckIn || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Anomalies List */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="px-6 py-12 text-center">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-gray-600">Chargement...</span>
              </div>
            </div>
          ) : anomalies.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600 text-lg font-medium">Aucune anomalie détectée</p>
              <p className="text-gray-500 text-sm mt-2">
                Toutes les fiches police sont à jour
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {anomalies.map((anomaly) => (
                <div
                  key={anomaly.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${getSeverityColor(
                    anomaly.daysSinceCheckIn
                  )} border-l-4`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center mb-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            anomaly.daysSinceCheckIn >= 60
                              ? 'bg-red-100 text-red-800'
                              : anomaly.daysSinceCheckIn >= 45
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {getSeverityBadge(anomaly.daysSinceCheckIn)}
                        </span>
                        {anomaly.isGuestStillPresent && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <UserIcon className="h-3 w-3 mr-1" />
                            Client Présent
                          </span>
                        )}
                      </div>

                      {/* Guest Information */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="flex items-center">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-xs text-gray-500">Nom du Client</p>
                            <p className="text-sm font-medium text-gray-900">
                              {anomaly.guestName}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <HomeIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-xs text-gray-500">Chambre</p>
                            <p className="text-sm font-medium text-gray-900">
                              {anomaly.roomNumber}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <p className="text-xs text-gray-500">Date d'Arrivée</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatDate(anomaly.checkInDate)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Anomaly Details */}
                      <div className="bg-white rounded-md p-3 mb-3">
                        <div className="flex items-start">
                          <ClockIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              {anomaly.reason}
                            </p>
                            <p className="text-xs text-gray-600">
                              Détecté le {formatDateTime(anomaly.detectedAt)}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              <span className="font-semibold">
                                {anomaly.daysSinceCheckIn} jours
                              </span>{' '}
                              depuis l'arrivée
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="ml-6 flex flex-col space-y-2">
                      <button
                        onClick={() => handleViewDetails(anomaly.policeFileId)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        <EyeIcon className="h-4 w-4 mr-2" />
                        Voir Détails
                      </button>

                      <button
                        onClick={() => handleResolve(anomaly.id)}
                        disabled={resolving === anomaly.id}
                        className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resolving === anomaly.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Résolution...
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            Marquer Résolu
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resolve Error Message */}
        {resolveError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-sm text-red-800">{resolveError}</p>
              </div>
              <button onClick={() => setResolveError(null)} className="text-red-600 hover:text-red-800 text-sm font-medium">Fermer</button>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmResolveId && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => !resolving && setConfirmResolveId(null)}></div>
              <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
                <div className="flex items-center mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mr-3" />
                  <h3 className="text-lg font-medium text-gray-900">Confirmer la resolution</h3>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Etes-vous sur de vouloir marquer cette anomalie comme resolue ?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setConfirmResolveId(null)}
                    disabled={!!resolving}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmResolve}
                    disabled={!!resolving}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {resolving ? 'Resolution...' : 'Confirmer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Text */}
        {!loading && anomalies.length > 0 && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  À propos des anomalies
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Les anomalies sont automatiquement détectées pour les fiches police en
                    statut "Ouverte" depuis plus de 30 jours sans facture uploadée.
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>
                      <strong>Moyen:</strong> 30-44 jours depuis l'arrivée
                    </li>
                    <li>
                      <strong>Élevé:</strong> 45-59 jours depuis l'arrivée
                    </li>
                    <li>
                      <strong>Critique:</strong> 60+ jours depuis l'arrivée
                    </li>
                  </ul>
                  <p className="mt-2">
                    Les anomalies sont automatiquement résolues lorsque la facture est
                    uploadée ou que le départ du client est confirmé.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
